import { useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading, PageHeader } from "@/components/States";
import { assemblyRate } from "@/components/project/EstimateTab";

const FIELDS = {
  prices: [["product_name", "Product"], ["product_code", "Code"], ["category", "Category"], ["supplier", "Supplier"], ["unit_cost", "Unit cost", "number"], ["unit_type", "Unit"], ["price_date", "Price date", "date"]],
  suppliers: [["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["website", "Website"], ["notes", "Notes"]],
  paints: [["brand", "Brand"], ["name", "Name"], ["code", "Code"], ["hex", "Hex"], ["color_family", "Family"], ["lrv", "LRV", "number"]],
};

function EditDialog({ kind, item, onClose, onSaved }) {
  const [f, setF] = useState(item || {});
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f };
    FIELDS[kind].forEach(([k, , t]) => { if (t === "number") body[k] = Number(body[k]) || 0; });
    try {
      if (item?.id) await api.put(`/${kind}/${item.id}`, body); else await api.post(`/${kind}`, body);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="section-title font-normal">{item?.id ? "Edit" : "Add"} {kind.slice(0, -1)}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" data-testid={`${kind}-edit-form`}>
          {FIELDS[kind].map(([k, l, t]) => (
            <div key={k} className="space-y-1"><Label htmlFor={`f-${k}`}>{l}</Label>
              <Input id={`f-${k}`} type={t || "text"} step="any" required={k === FIELDS[kind][0][0]} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-field-${k}`} /></div>
          ))}
          <Button type="submit" className="rounded-full sm:col-span-2" data-testid={`${kind}-save-button`}>Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Table({ kind, render, filterKey }) {
  const { data, error, loading, reload } = useFetch(`/${kind}`);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const del = async (it) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/${kind}/${it.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <Loading rows={4} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const list = data.filter((d) => filterKey(d).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative max-w-xs flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${kind}-search`} /></div>
        <Button variant="outline" onClick={() => setEdit({})} data-testid={`${kind}-add-button`}><Plus className="mr-1 h-4 w-4" />Add</Button>
        <span className="ml-auto self-center font-mono text-xs text-muted-foreground">{list.length} entries</span>
      </div>
      {list.length === 0 ? <Empty title="Nothing found" text="Try a different search." /> : render(list, setEdit, del)}
      {edit && <EditDialog kind={kind} item={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}

const Actions = ({ it, setEdit, del, kind }) => (
  <span className="flex shrink-0 gap-1">
    <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setEdit(it)} data-testid={`${kind}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
    <Button size="icon" variant="ghost" aria-label="Delete" onClick={() => del(it)} data-testid={`${kind}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
  </span>
);

const priceRows = (list, setEdit, del) => (
  <div className="overflow-x-auto rounded-xl border bg-card"><table className="w-full text-sm" data-testid="prices-table">
    <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground"><tr>{["Product", "Code", "Category", "Unit cost", "Unit", "Verified", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
    <tbody>{list.map((p) => (
      <tr key={p.id} className="border-b last:border-0 hover:bg-secondary/40">
        <td className="px-4 py-2.5">{p.product_name}</td><td className="px-4 font-mono text-xs">{p.product_code}</td><td className="px-4 text-muted-foreground">{p.category}</td>
        <td className="px-4 font-mono">{money(p.unit_cost)}</td><td className="px-4 text-muted-foreground">{p.unit_type}</td><td className="px-4 font-mono text-xs">{p.price_date}</td>
        <td className="px-2"><Actions it={p} setEdit={setEdit} del={del} kind="prices" /></td>
      </tr>))}</tbody></table></div>
);

const supplierRows = (list, setEdit, del) => (
  <div className="grid gap-4 md:grid-cols-2">{list.map((s) => (
    <div key={s.id} className="flex gap-3 rounded-xl border bg-card p-5">
      <div className="min-w-0 flex-1 space-y-1"><p className="section-title">{s.name}{s.is_preferred && <span className="ml-2 align-middle text-[10px] uppercase tracking-widest text-accent">Preferred</span>}</p>
        <p className="text-xs text-muted-foreground">{[s.phone, s.email, s.address].filter(Boolean).join(" · ")}</p>
        {s.notes && <p className="text-sm text-muted-foreground">{s.notes}</p>}</div>
      <Actions it={s} setEdit={setEdit} del={del} kind="suppliers" />
    </div>))}</div>
);

const paintRows = (list, setEdit, del) => (
  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6" data-testid="paints-grid">{list.map((p) => (
    <div key={p.id} className="group overflow-hidden rounded-lg border bg-card">
      <div className="h-24" style={{ background: p.hex }} />
      <div className="flex items-start justify-between p-2.5"><div className="min-w-0"><p className="truncate text-sm font-medium">{p.name}</p><p className="truncate font-mono text-[10px] text-muted-foreground">{p.brand} · {p.code} · LRV {p.lrv}</p></div>
        <span className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"><Actions it={p} setEdit={setEdit} del={del} kind="paints" /></span></div>
    </div>))}</div>
);

function Assemblies() {
  const { data, error, loading, reload } = useFetch("/assemblies");
  if (loading) return <Loading rows={3} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="assemblies-list">{data.map((a) => (
      <div key={a.id} className="space-y-3 rounded-xl border bg-card p-5">
        <div className="flex items-start justify-between gap-2"><p className="section-title">{a.name}</p><p className="shrink-0 font-mono text-sm">{money(assemblyRate(a))}<span className="text-xs text-muted-foreground"> / sq ft</span></p></div>
        <p className="text-sm text-muted-foreground">{a.description}</p>
        <ul className="space-y-1 border-t pt-3 font-mono text-xs">{a.materials.map((m, i) => <li key={m.id || `${m.name}-${i}`} className="flex justify-between gap-2"><span className="truncate">{m.name}</span><span>{m.qty_per_unit}/sq ft × {money(m.unit_cost)} +{m.waste_pct}%</span></li>)}</ul>
      </div>))}</div>
  );
}

export default function PriceBook() {
  return (
    <div className="p-6 md:p-10 lg:p-12">
      <PageHeader eyebrow="Muskoka Lumber & paint library" title="Price book" />
      <Tabs defaultValue="prices">
        <TabsList className="mb-6">
          <TabsTrigger value="prices" data-testid="pricebook-tab-prices">Prices</TabsTrigger>
          <TabsTrigger value="assemblies" data-testid="pricebook-tab-assemblies">Assemblies</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="pricebook-tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="paints" data-testid="pricebook-tab-paints">Paint colours</TabsTrigger>
        </TabsList>
        <TabsContent value="prices"><Table kind="prices" render={priceRows} filterKey={(p) => `${p.product_name} ${p.product_code} ${p.category}`} /></TabsContent>
        <TabsContent value="assemblies"><Assemblies /></TabsContent>
        <TabsContent value="suppliers"><Table kind="suppliers" render={supplierRows} filterKey={(s) => s.name} /></TabsContent>
        <TabsContent value="paints"><Table kind="paints" render={paintRows} filterKey={(p) => `${p.brand} ${p.name} ${p.code} ${p.color_family}`} /></TabsContent>
      </Tabs>
    </div>
  );
}
